import { Grid, makeStyles, Typography } from '@material-ui/core';
import {
  CancelablePromise,
  CustomTerminologiesService,
  CustomTerminology,
} from 'api-clients/monolith';
import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  VFC,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { makeSelectWhoami } from 'containers/Auth_old/selectors';
import { addMessageAction } from 'containers/FlashMessage/actions';
import { useApiServiceMutation } from 'hooks/useApiServiceMutation';
import { usePostHog } from 'hooks/usePostHog';
import CheckmarkIcon from 'images/checkmark_white.svg';
import DeleteIcon from 'images/delete-upload.svg';
import DownloadIcon from 'images/download-icon.svg';
import { greenMark } from 'theme';

import { messages } from './messages';
import { coerceTerminologyFileType, getMessageFromResult } from './util';

// ********************************************************************************
// == Constant ====================================================================
const REPLACE_INPUT_ID = 'replace-input';

const useStyles = makeStyles(theme => ({
  actionButton: {
    background: 'none',
    '& img': {
      height: '16px',
      '&:hover': {
        cursor: 'pointer',
      },
    },
    border: 'none',
  },
  actionButtonDisabled: {
    background: 'none',
    '& img': {
      height: '16px',
      filter: 'grayscale(100%)',
    },
    border: 'none',
    cursor: 'not-allowed',
  },
  actionsContainer: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  fileName: {
    color: theme.palette.common.blueAppBarBackground,
  },
  previewIconContainer: {
    background: greenMark,
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    marginRight: theme.spacing(2),
    padding: '4px',
    height: 16,
    width: 16,
  },
  previewContainer: {
    backgroundColor: theme.palette.common.gray100,
    borderRadius: `${theme.misc.borderRadius}px`,
    height: '100%',
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    width: '100%',
  },
  previewDetails: {
    alignItems: 'center',
    display: 'flex',
    flex: '1 1 16px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  replaceAction: {
    color: theme.palette.common.blue400,
    cursor: 'pointer',
  },
  replaceActionDisabled: {
    color: theme.palette.common.gray400,
    cursor: 'not-allowed',
  },
}));

// == Type ========================================================================
type DeleteFunction = (
  accountExternalId: string,
  id: string,
) => CancelablePromise<void>;

type CustomTerminologyObject = {
  // eslint-disable-next-line camelcase
  custom_terminology: CustomTerminology;
};

type Props = {
  accountExternalId: string;
  setActiveTerminology: Dispatch<SetStateAction<CustomTerminology | null>>;
  terminology: CustomTerminology | null;
};

type UpdateFunction = (
  accountExternalId: string,
  id: string,
  requestBody: string,
) => CancelablePromise<CustomTerminologyObject>;

// == Component ===================================================================
const CustomTerminologyFilePreview: VFC<Props> = ({
  accountExternalId,
  setActiveTerminology,
  terminology,
}: Props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();
  const posthog = usePostHog();
  const whoami = useSelector(makeSelectWhoami());

  // -- Callback ------------------------------------------------------------------
  const downloadTerminology = useCallback(
    () =>
      CustomTerminologiesService.getInternalApiCustomTerminologiesTerminologiesDownloadUrl(
        accountExternalId,
        terminology?.external_id ?? '',
      ),
    [accountExternalId, terminology?.external_id],
  );

  // -- Mutation ------------------------------------------------------------------
  const { mutation: deleteTerminology, result: deleteResult } =
    useApiServiceMutation<void, DeleteFunction, void>(
      (accountExternalId: string, id: string) =>
        CustomTerminologiesService.deleteInternalApiCustomTerminologiesTerminologies(
          accountExternalId,
          id,
        ),
      {
        onError: result =>
          dispatch(
            addMessageAction(getMessageFromResult(result, intl), 'error'),
          ),

        onSuccess: () => {
          dispatch(
            addMessageAction(
              intl.formatMessage(messages.deleteSuccess),
              'success',
            ),
          );
          setActiveTerminology(null);
        },
      },
    );

  const { mutation: updateTerminology, result: updateResult } =
    useApiServiceMutation<CustomTerminologyObject, UpdateFunction, void>(
      (accountExternalId: string, id: string, requestBody: string) =>
        CustomTerminologiesService.patchInternalApiCustomTerminologiesTerminologies(
          accountExternalId,
          id,
          requestBody,
        ),
      {
        onError: result =>
          dispatch(
            addMessageAction(getMessageFromResult(result, intl), 'error'),
          ),

        onSuccess: updatedTerminologyObj => {
          dispatch(
            addMessageAction(
              intl.formatMessage(messages.processSuccess),
              'success',
            ),
          );
          setActiveTerminology(updatedTerminologyObj.custom_terminology);
        },
      },
    );

  // -- Handler -------------------------------------------------------------------
  const downloadFile = async () => {
    if (!terminology) {
      return;
    }

    const urlObject = await downloadTerminology();

    const link = document.createElement('a');
    link.href = urlObject.url;
    link.download = terminology.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const removeUploadedFile = () => {
    if (!terminology) {
      return;
    }

    deleteTerminology(accountExternalId, terminology.external_id);
  };

  const handleClickReplaceFile = () => {
    posthog.capture('custom_terminology:upload_replace_click', {
      account_id: whoami.account_id,
      user_id: whoami.external_id,
    });
    const fileInput = document.getElementById(REPLACE_INPUT_ID);
    fileInput?.click();
  };

  const handleReplaceFile = (e: ChangeEvent<HTMLInputElement>) => {
    try {
      if (!terminology) {
        return;
      }
      if (!e.target.files?.[0]) {
        return;
      }

      const file = e.target.files?.[0];
      updateTerminology(
        accountExternalId,
        terminology.external_id,
        coerceTerminologyFileType(file),
      );
    } finally {
      const fileInput = e.target as HTMLInputElement;
      fileInput.value = '';
    }
  };

  // -- UI ------------------------------------------------------------------------
  if (!terminology) {
    return null;
  }
  const { name } = terminology;

  const isLoading =
    updateResult.status === 'loading' || deleteResult.status === 'loading';
  return (
    <Grid
      alignItems="center"
      className={classes.previewContainer}
      container
      direction="column"
    >
      <Grid alignItems="center" container justify="space-between">
        <Grid className={classes.previewDetails} title={name}>
          <span className={classes.previewIconContainer}>
            <img alt="active-icon" src={CheckmarkIcon} />
          </span>

          <Typography
            className={classes.fileName}
            display="inline"
            noWrap
            variant="body2"
          >
            {name}&nbsp;
          </Typography>
        </Grid>

        <div className={classes.actionsContainer}>
          <input
            accept=".csv"
            id={REPLACE_INPUT_ID}
            onChange={handleReplaceFile}
            style={{ display: 'none' }}
            type="file"
          />
          <Typography
            className={
              isLoading ? classes.replaceActionDisabled : classes.replaceAction
            }
            onClick={isLoading ? undefined /* no op */ : handleClickReplaceFile}
            variant="body1"
          >
            <FormattedMessage {...messages.replace} />
          </Typography>

          <button
            className={
              isLoading ? classes.actionButtonDisabled : classes.actionButton
            }
            disabled={isLoading}
            type="button"
            onClick={() => void downloadFile()}
          >
            <img alt="download" src={DownloadIcon} />
          </button>

          <button
            className={
              isLoading ? classes.actionButtonDisabled : classes.actionButton
            }
            disabled={isLoading}
            type="button"
            onClick={removeUploadedFile}
          >
            <img alt="delete" src={DeleteIcon} />
          </button>
        </div>
      </Grid>
    </Grid>
  );
};

// == Export ======================================================================
export default CustomTerminologyFilePreview;
